import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface DailyAttentionState {
  /** key -> fecha ('YYYY-MM-DD') del último día en que se descartó la señal */
  dismissedOn: Record<string, string>;
  dismissToday: (key: string) => void;
}

const useDailyAttentionStore = create<DailyAttentionState>()(
  persist(
    (set) => ({
      dismissedOn: {},
      dismissToday: (key) => set((s) => ({ dismissedOn: { ...s.dismissedOn, [key]: todayKey() } })),
    }),
    { name: 'senciyo-attention-daily' }
  )
);

function todayKey(): string {
  return new Date().toISOString().slice(0, 10);
}

function dentroDeVentana(anchorDate: string, windowDays: number): boolean {
  const limite = new Date(anchorDate);
  limite.setDate(limite.getDate() + windowDays);
  return new Date() < limite;
}

/**
 * Señal de atención (ej. AttentionPulse) que se repite una vez por día durante
 * `windowDays` días desde `anchorDate`. A diferencia de useFirstTimeAttention,
 * un clic solo la apaga por el día de hoy: al día siguiente vuelve a activarse
 * si sigue dentro de la ventana. Pasado ese plazo, deja de mostrarse para siempre.
 *
 * `anchorDate` (fecha ISO) es el inicio de la ventana; null si aún no aplica.
 */
export function useTimeBoxedDailyAttention(key: string, anchorDate: string | null, windowDays: number) {
  const dismissedToday = useDailyAttentionStore((s) => s.dismissedOn[key] === todayKey());
  const dismissToday = useDailyAttentionStore((s) => s.dismissToday);

  const active = anchorDate !== null && dentroDeVentana(anchorDate, windowDays) && !dismissedToday;

  return {
    active,
    markSeenToday: () => dismissToday(key),
  };
}
